import { Outlet, Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, Shield } from "lucide-react";
import { SITE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import ThemeToggle from "@/components/ThemeToggle";
import SteamLoginButton from "@/components/SteamLoginButton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/", label: "Accueil", end: true },
  { to: "/reglement", label: "Règlement" },
  { to: "/forum", label: "Forum" },
  { to: "/boutique", label: "Boutique" },
  { to: "/sanctions", label: "Sanctions" },
  { to: "/tickets", label: "Tickets", authOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { data: cfg } = useSiteConfig();
  const [open, setOpen] = useState(false);

  const NavLinks = ({ onClick }) => (
    <>
      {NAV.filter((n) => !n.authOnly || user).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          data-testid={`nav-${n.to.slice(1) || "home"}`}
          className={({ isActive }) =>
            `font-display text-sm uppercase tracking-wider px-3 py-2 rounded-md transition-colors ${
              isActive
                ? "text-primary bg-primary/10"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`
          }
        >
          {n.label}
        </NavLink>
      ))}
      {cfg?.workshop_url && (
        <a
          href={cfg.workshop_url}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
          data-testid="nav-workshop"
          className="font-display text-sm uppercase tracking-wider px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          Workshop ↗
        </a>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground grain">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={SITE.logoUrl} alt="RiverSide Roleplay" className="h-11 w-auto" />
            <span className="sr-only">RiverSide Roleplay</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <NavLinks />
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={SITE.discordUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-discord-button"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#5865F2] hover:bg-[#4752c4] text-white text-sm font-medium"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.955 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              <span className="hidden md:inline">Discord</span>
            </a>

            <ThemeToggle />

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-button"
                    className="flex items-center gap-2 pl-1 pr-3 h-9 rounded-full border border-border hover:border-primary/60"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user.avatar_url} alt={user.persona_name} />
                      <AvatarFallback className="text-xs">
                        {user.persona_name?.[0]?.toUpperCase() ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm max-w-[120px] truncate">
                      {user.persona_name}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="truncate">{user.persona_name}</span>
                      <span className="text-xs text-muted-foreground font-mono-tech">
                        {user.role_label || user.role}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {["fondateur", "co_fondateur", "superadmin", "admin", "moderateur_plus", "moderateur", "moderateur_essai"].includes(user.role) && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="menu-admin-link" className="cursor-pointer">
                        <Shield className="h-4 w-4 mr-2" /> Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-testid="logout-button"
                    onClick={logout}
                    className="cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SteamLoginButton className="hidden sm:inline-flex" label="Steam" />
            )}

            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-border bg-background/95">
            <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              <NavLinks onClick={() => setOpen(false)} />
              {!user && <SteamLoginButton className="mt-2" />}
              <a
                href={SITE.discordUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex justify-center items-center h-10 rounded-md bg-[#5865F2] text-white"
                onClick={() => setOpen(false)}
              >
                Rejoindre le Discord
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 relative z-10">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-secondary/30 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-4">
          <div>
            <img src={SITE.logoUrl} alt="RiverSide Roleplay" className="h-12 w-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Serveur Garry's Mod DarkRP français. Votre histoire commence à RiverSide.
            </p>
          </div>
          <div>
            <h4 className="font-display uppercase text-sm tracking-widest mb-3">Navigation</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/" className="hover:text-primary">Accueil</Link></li>
              <li><Link to="/reglement" className="hover:text-primary">Règlement</Link></li>
              <li><Link to="/forum" className="hover:text-primary">Forum</Link></li>
              <li><Link to="/boutique" className="hover:text-primary">Boutique</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display uppercase text-sm tracking-widest mb-3">Communauté</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href={SITE.discordUrl} target="_blank" rel="noreferrer" className="hover:text-primary">Discord</a></li>
              <li><a href={SITE.connectUrl} className="hover:text-primary">Rejoindre le serveur</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display uppercase text-sm tracking-widest mb-3">Serveur</h4>
            <p className="font-mono-tech text-sm text-primary">{SITE.serverIp}</p>
            <p className="text-xs text-muted-foreground mt-2">DarkRP · Français · 24/7</p>
          </div>
        </div>
        <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} RiverSide Roleplay — Non affilié à Valve ou Facepunch Studios.
        </div>
      </footer>
    </div>
  );
}
